import { useState, useCallback, useRef, useEffect } from "react";
import type { AudioSource, AudioSourceType } from "../hooks/useAudioAnalyzer";
import { captureMicrophone, captureUnavailableReason, captureErrorMessage, createPerformanceAudioContext } from "../audio/capture";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { ArrowUpRightIcon } from "@phosphor-icons/react/dist/csr/ArrowUpRight";
import { FileAudioIcon } from "@phosphor-icons/react/dist/csr/FileAudio";
import { BroadcastIcon } from "@phosphor-icons/react/dist/csr/Broadcast";
import { MicrophoneIcon } from "@phosphor-icons/react/dist/csr/Microphone";
import { CheckCircleIcon } from "@phosphor-icons/react/dist/csr/CheckCircle";
import "../styles/source-entry.css";
import { InstallApp } from "../pwa/InstallApp";

interface UploadModalProps { onAudioSourceSelect: (source: AudioSource) => void; }

export function UploadModal({ onAudioSourceSelect }: UploadModalProps) {
  const [sourceMode, setSourceMode] = useState<AudioSourceType>("file");
  const [fileError, setFileError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [capturedStream, setCapturedStream] = useState<MediaStream | null>(null);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [enableLookAhead, setEnableLookAhead] = useState(false);
  const [isBrowserTab, setIsBrowserTab] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const handedOff = useRef(false);
  const mounted = useRef(true);
  const pending = useRef(false);
  const preview = useRef<AudioContext | null>(null);
  const unsupported = sourceMode === "file" ? null : captureUnavailableReason(sourceMode, window.isSecureContext, navigator.mediaDevices);

  const stopPreview = useCallback(() => {
    if (preview.current && preview.current.state !== "closed") void preview.current.close().catch(() => {});
    preview.current = null;
  }, []);
  const handleStopCapture = useCallback(() => {
    stopPreview();
    const stream = streamRef.current;
    streamRef.current = null;
    stream?.getTracks().forEach(track => { track.onended = null; track.stop(); });
    setCapturedStream(null);
  }, [stopPreview]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      stopPreview();
      if (!handedOff.current) streamRef.current?.getTracks().forEach(track => { track.onended = null; track.stop(); });
    };
  }, [stopPreview]);

  const chooseFile = (file?: File) => {
    if (!file || pending.current) return;
    if (!file.type.startsWith("audio/") && !/\.(mp3|wav|ogg|flac|m4a|aac|aiff?|opus|webm)$/i.test(file.name)) {
      setFileError("请选择音频文件，例如 MP3、WAV 或 M4A。"); return;
    }
    handleStopCapture(); setSelectedFile(file); setSourceMode("file"); setFileError(null); setCaptureError(null);
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    chooseFile(event.target.files?.[0]); event.target.value = "";
  };
  const handleDragOver = (event: React.DragEvent) => { event.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (event: React.DragEvent) => { event.preventDefault(); setIsDragging(false); };
  const handleDrop = (event: React.DragEvent) => { event.preventDefault(); setIsDragging(false); chooseFile(event.dataTransfer.files[0]); };
  const handleFileClick = () => fileInputRef.current?.click();

  const handleCapture = async () => {
    if (pending.current || sourceMode === "file") return;
    if (unsupported) { setCaptureError(unsupported); return; }
    pending.current = true; setIsCapturing(true); setCaptureError(null);
    // Unlock optional tab monitoring while still inside the tap gesture.
    let monitor: AudioContext | null = null;
    let acquired: MediaStream | null = null;
    try {
      if (sourceMode === "system" && enableLookAhead) {
        monitor = createPerformanceAudioContext(); preview.current = monitor;
        void monitor.resume().catch(() => {});
      }
      acquired = sourceMode === "microphone" ? await captureMicrophone(navigator.mediaDevices) :
        await navigator.mediaDevices.getDisplayMedia({ video: true, audio: {
          echoCancellation: false, noiseSuppression: false, autoGainControl: false,
          // Chromium tab takeover; ignored by browsers that do not support it.
          // @ts-ignore experimental constraint
          suppressLocalAudioPlayback: enableLookAhead,
        } });
      if (!mounted.current) { acquired.getTracks().forEach(track => track.stop()); return; }
      if (!acquired.getAudioTracks().some(track => track.readyState === "live")) {
        throw new Error("没有收到共享音频。请改用麦克风，或在支持音频共享的浏览器里勾选「共享音频」。");
      }
      const browserTab = acquired.getVideoTracks()[0]?.getSettings().displaySurface === "browser";
      acquired.getVideoTracks().forEach(track => track.stop());
      if (monitor) monitor.createMediaStreamSource(acquired).connect(monitor.destination);
      streamRef.current = acquired;
      setCapturedStream(acquired); setIsBrowserTab(browserTab); setSelectedFile(null);
      acquired.getAudioTracks().forEach(track => { track.onended = () => {
        if (streamRef.current === acquired && !handedOff.current) handleStopCapture();
      }; });
    } catch (error) {
      acquired?.getTracks().forEach(track => track.stop()); stopPreview();
      if (mounted.current) setCaptureError(captureErrorMessage(error));
    } finally {
      pending.current = false;
      if (mounted.current) setIsCapturing(false);
    }
  };
  const chooseSource = (mode: AudioSourceType) => {
    if (pending.current || mode === sourceMode) return;
    handleStopCapture(); setSourceMode(mode); setCaptureError(null); setFileError(null);
  };
  const hasAudioSource = sourceMode === "file" ? !!selectedFile : !!capturedStream;
  const handleStart = () => {
    if (!hasAudioSource || pending.current || handedOff.current) return;
    if (sourceMode !== "file" && !capturedStream?.getAudioTracks().some(track => track.readyState === "live")) {
      handleStopCapture(); setCaptureError("音源已断开，请重新连接。"); return;
    }
    try {
      const audioContext = createPerformanceAudioContext();
      // Must happen synchronously in the click, before React mounts the audio graph on Safari.
      void audioContext.resume().catch(() => {});
      stopPreview(); handedOff.current = true;
      capturedStream?.getTracks().forEach(track => { track.onended = null; });
      onAudioSourceSelect(sourceMode === "file" ? { type: "file", file: selectedFile!, label: selectedFile!.name, audioContext } : {
        type: sourceMode, stream: capturedStream!, audioContext,
        label: sourceMode === "microphone" ? capturedStream!.getAudioTracks()[0]?.label || "麦克风" : isBrowserTab ? "Browser Tab" : "System Audio",
        enableLookAhead: sourceMode === "system" && enableLookAhead,
      });
    } catch (error) { setCaptureError(captureErrorMessage(error)); }
  };

  return (
    <main className="source-entry">
      <header className="source-header">
        <div className="source-brand" aria-label="BASS Performance">
          <strong>BASS</strong><span>PERFORMANCE</span>
        </div>
        <InstallApp />
      </header>

      <div className="source-main">
        <section className="source-hero" aria-labelledby="source-title">
          <p className="source-kicker">SOUND / SPACE / MOTION</p>
          <h1 id="source-title">BASS</h1>
          <div className="source-hero-subtitle">PERFORMANCE</div>
          <div className="source-intro">
            <p>由声音驱动的实时光场。</p>
            <span>选择一首音乐，或接入正在播放的声音。</span>
          </div>
        </section>

        <section className="source-console" aria-labelledby="source-heading">
          <div className="source-console-heading">
            <h2 id="source-heading">选择音源</h2>
            <span>INPUT / SETUP</span>
          </div>

          <div className="source-choices" role="group" aria-label="音源类型">
            <button type="button" aria-pressed={sourceMode === "file"} disabled={isCapturing}
              aria-controls="source-input-workspace" onClick={() => chooseSource("file")}>
              <span className="source-choice-number">01</span><span>本地文件</span>
            </button>
            <button type="button" aria-pressed={sourceMode === "system"} disabled={isCapturing}
              aria-controls="source-input-workspace" onClick={() => chooseSource("system")}>
              <span className="source-choice-number">02</span><span>系统音频</span>
            </button>
            <button type="button" aria-pressed={sourceMode === "microphone"} disabled={isCapturing}
              aria-controls="source-input-workspace" onClick={() => chooseSource("microphone")}>
              <span className="source-choice-number">03</span><span>麦克风</span>
            </button>
          </div>

          <div id="source-input-workspace" className="source-workspace">
            {sourceMode === "file" ? (
              <>
                <input ref={fileInputRef} type="file" accept="audio/*,.mp3,.wav,.ogg,.flac,.m4a,.aac,.aif,.aiff,.opus,.webm"
                  onChange={handleFileChange} className="source-file-input" tabIndex={-1} aria-label="选择音频文件" />
                <button type="button" className="source-drop-zone" data-dragging={isDragging}
                  data-selected={!!selectedFile} onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={handleFileClick}
                  aria-label={selectedFile ? "更换音频文件" : "选择音频文件"}
                  aria-describedby={fileError ? "source-file-error" : undefined}>
                  <div className="source-drop-top">
                    <FileAudioIcon size={27} weight="light" />
                    <span>{selectedFile ? "FILE SELECTED" : "LOCAL AUDIO"}</span>
                    {selectedFile ? <CheckCircleIcon size={19} /> : <ArrowUpRightIcon size={19} />}
                  </div>
                  <div className="source-drop-content">
                    <strong className={selectedFile ? "source-file-name" : undefined} title={selectedFile?.name}>
                      {selectedFile ? selectedFile.name : "选择一首音乐"}
                    </strong>
                    <span>{selectedFile
                      ? (selectedFile.size / 1024 / 1024).toFixed(2) + " MB"
                      : "或点击这里选择音频文件"}</span>
                  </div>
                  <div className="source-drop-bottom">
                    <span>{selectedFile ? "点击更换文件" : "MP3 / WAV / FLAC / M4A"}</span>
                    <ArrowRightIcon size={17} />
                  </div>
                </button>
                {fileError && <p className="source-error" id="source-file-error" role="alert">{fileError}</p>}
                <p className="source-help">文件在本地播放，无需上传。</p>
                {captureError && <p className="source-error" role="alert">{captureError}</p>}
              </>
            ) : (
              <div className="source-live">
                <div className="source-live-heading">
                  {capturedStream ? <CheckCircleIcon size={27} weight="light" /> : sourceMode === "microphone" ? <MicrophoneIcon size={27} weight="light" /> : <BroadcastIcon size={27} weight="light" />}
                  <span>{capturedStream ? "CONNECTED" : "LIVE AUDIO"}</span>
                </div>
                <h3>{capturedStream ? "音源已连接" : sourceMode === "microphone" ? "让现场声音驱动光场" : "接入正在播放的声音"}</h3>
                <p className="source-live-description">
                  {capturedStream
                    ? (sourceMode === "microphone" ? "麦克风 · 实时输入 · 不回放声音" : (isBrowserTab ? "浏览器标签页" : "系统音频") + (enableLookAhead ? " · Look Ahead 已开启" : " · 实时输入"))
                    : sourceMode === "microphone" ? "使用手机或电脑麦克风收音，实时驱动画面。允许麦克风权限后即可进入演出。" : "在共享窗口中选择音源，并勾选「共享音频」。"}
                </p>

                {sourceMode === "system" && !capturedStream && <label className="source-look-ahead">
                  <input type="checkbox" checked={enableLookAhead} disabled={isCapturing}
                    onChange={event => setEnableLookAhead(event.target.checked)} />
                  <span><strong>Look Ahead</strong><span>让画面提前响应</span></span>
                </label>}
                {sourceMode === "system" && !capturedStream && enableLookAhead && <p className="source-help source-look-ahead-note">启用时请选择 Chrome 标签页。</p>}

                <button type="button" className="source-capture-button"
                  onClick={capturedStream ? handleStopCapture : handleCapture}
                  disabled={isCapturing || (!!unsupported && !capturedStream)}>
                  <span>{capturedStream ? "断开连接" : isCapturing ? "正在等待音源…" : sourceMode === "microphone" ? "启用麦克风" : "连接音源"}</span>
                  {!capturedStream && !isCapturing && <ArrowUpRightIcon size={17} />}
                </button>
                {(captureError || unsupported) && <p className="source-error" role="alert">{captureError || unsupported}</p>}
              </div>
            )}
          </div>

          <div className="source-action">
            <div className="source-readiness" data-ready={hasAudioSource} role="status">
              <span>{hasAudioSource ? "准备就绪" : isCapturing ? "等待授权" : "等待音源"}</span>
              <span>{sourceMode === "file" ? "FILE" : "LIVE"}</span>
            </div>
            <button type="button" className="source-start" onClick={handleStart} disabled={!hasAudioSource || isCapturing}>
              <span>进入演出</span><ArrowRightIcon size={22} />
            </button>
          </div>
        </section>
      </div>

      <footer className="source-footer">
        <span className="source-footer-label">演出控制</span>
        <span className="source-touch-help">双击画面，显示 / 隐藏控制面板</span>
        <div className="source-shortcuts" aria-label="演出快捷键说明">
          <span><kbd>Space</kbd>播放 / 暂停</span>
          <span><kbd>1–4</kbd>切换效果</span>
          <span><kbd>R</kbd>独立旋转</span>
          <span><kbd>H</kbd>隐藏界面</span>
          <span><kbd>F</kbd>全屏</span>
        </div>
      </footer>
    </main>
  );
}
